import { useRouter } from 'next/router';
import { useQuery } from '@apollo/react-hooks';
import get from 'lodash/get';
import _ from 'lodash';

import individualFilteredProduct from 'shared/graphql/product/individual-filtered-product.gql';
import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';

import { products as mockProducts } from 'src/tests';
import { parseProduct } from 'utils/helpers/product';
import useDispensary, { getDispensary } from 'src/dispensary/hooks/use-dispensary';
import { useHardAgeGate } from 'hooks/use-hard-age-gate';
import { useHidePotenciesAndStrains } from 'hooks/use-hide-potencies-and-strains';
import useUI from './use-ui';

export const getVariables = (productId, dispensary, isKiosk, includeTerpenes, includeCannabinoids) => ({
  productsFilter: {
    cName: productId,
    dispensaryId: dispensary?.id,
    removeProductsBelowOptionThresholds: true,
    isKioskMenu: isKiosk,
    bypassKioskThresholds: isKiosk,
    bypassOnlineThresholds: true,
  },
  includeTerpenes,
  includeCannabinoids,
});

export const getProduct = async (apolloClient, props) => {
  const { data } = await getDispensary(apolloClient, props);
  const dispensary = get(data, `filteredDispensaries[0]`);

  return apolloClient.query({
    query: individualFilteredProduct,
    variables: getVariables(props.params.productId, dispensary),
  });
};

export const getDispensaryProduct = async (apolloClient, productId, dispensary) => {
  const { data } = await apolloClient.query({
    query: individualFilteredProduct,
    variables: getVariables(productId, dispensary, false, false, false),
  });

  return parseProduct(get(data, `filteredProducts.products[0]`));
};

export default function useProduct() {
  const { query } = useRouter();
  const { dispensary } = useDispensary();
  const { hardAgeGate } = useHardAgeGate();
  const { menuHidePotencies } = useHidePotenciesAndStrains();
  // eslint-disable-next-line i18next/no-literal-string
  const terpeneFlagOn = useDispensaryFlag('rollout.terpenes', dispensary?.id);
  const includeTerpenes = !hardAgeGate && terpeneFlagOn;
  const includeCannabinoids = !menuHidePotencies;
  const UI = useUI();
  const { productId } = query;

  const variables = getVariables(productId, dispensary, UI.isKiosk, includeTerpenes, includeCannabinoids);

  // eslint-disable-next-line no-use-before-define
  const { data, ...apolloValues } = useQuery(individualFilteredProduct, {
    variables,
    skip: !productId,
  });

  // eslint-disable-next-line i18next/no-literal-string
  const product = parseProduct(get(data, 'filteredProducts.products[0]', _.find(mockProducts, { id: productId })));

  return { product, data, ...apolloValues };
}
